import WaIcon from "@awesome.me/webawesome-pro/dist/react/icon/index.js";
import React from "react";

const MIN_PASSENGERS = 1;
const MAX_PASSENGERS = 8;

type Props = {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
};

const clampPassengerCount = (value: number) =>
  Math.min(MAX_PASSENGERS, Math.max(MIN_PASSENGERS, Math.round(value)));

export const PassengerCountSelector: React.FC<Props> = ({ value, onChange, disabled }) => {
  const passengerCount = clampPassengerCount(value);
  const decrementDisabled = disabled || passengerCount <= MIN_PASSENGERS;
  const incrementDisabled = disabled || passengerCount >= MAX_PASSENGERS;
  const tariffGroup = passengerCount > 4 ? "5-8" : "1-4";

  const buttonStyle: React.CSSProperties = {
    width: 44,
    height: 44,
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.14)",
    background: "white",
    color: "rgba(15,23,42,0.82)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.56 : 1,
  };

  return (
    <div className="wa-stack wa-gap-s">
      <span className="wa-heading-m wa-color-neutral-600" style={{ pointerEvents: "none" }}>
        Passengers
      </span>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "44px minmax(0, 1fr) 44px",
          alignItems: "center",
          gap: 10,
          padding: "8px 10px",
          borderRadius: 10,
          background: "rgba(15,23,42,0.03)",
          border: "1px solid rgba(15,23,42,0.08)",
        }}
      >
        <button
          type="button"
          aria-label="Decrease passengers"
          disabled={decrementDisabled}
          onClick={() => onChange(clampPassengerCount(passengerCount - 1))}
          style={{
            ...buttonStyle,
            opacity: decrementDisabled ? 0.42 : buttonStyle.opacity,
            cursor: decrementDisabled ? "not-allowed" : buttonStyle.cursor,
          }}
        >
          <WaIcon name="minus" />
        </button>

        <div
          className="wa-stack wa-gap-3xs"
          aria-live="polite"
          style={{ textAlign: "center", minWidth: 0 }}
        >
          <span
            className="wa-heading-l"
            style={{ fontWeight: 800, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
          >
            {passengerCount}
          </span>
          <span className="wa-caption-s wa-color-neutral-600" style={{ lineHeight: 1.2 }}>
            {passengerCount === 1 ? "passenger" : "passengers"}
          </span>
        </div>

        <button
          type="button"
          aria-label="Increase passengers"
          disabled={incrementDisabled}
          onClick={() => onChange(clampPassengerCount(passengerCount + 1))}
          style={{
            ...buttonStyle,
            opacity: incrementDisabled ? 0.42 : buttonStyle.opacity,
            cursor: incrementDisabled ? "not-allowed" : buttonStyle.cursor,
          }}
        >
          <WaIcon name="plus" />
        </button>
      </div>

      <span className="wa-caption-s wa-color-neutral-500" style={{ pointerEvents: "none" }}>
        {tariffGroup} passenger tariff
      </span>
    </div>
  );
};
